import { useState } from "react";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { auth, db } from "../firebase";

async function upsertAffiliateProfile(user, extra = {}) {
  const payload = {
    name: extra.name || user.displayName || "",
    email: user.email || "",
    country: extra.country || "",
    refCode: extra.refCode || "",
    affiliate: true,
    balance: extra.balance || 0,
    createdAt: extra.createdAt || new Date(),
  };

  await Promise.all([
    setDoc(doc(db, "affiliate_users", user.uid), payload, { merge: true }),
    setDoc(doc(db, "users", user.uid), payload, { merge: true }),
  ]);
}

export default function AffiliateRegisterPage() {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    country: "",
    refCode: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const credentials = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(credentials.user, { displayName: form.name });
      await upsertAffiliateProfile(credentials.user, {
        name: form.name,
        country: form.country,
        refCode: form.refCode,
      });
      toast.success("Affiliate account created.");
    } catch (err) {
      toast.error(err?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const credentials = await signInWithEmailAndPassword(auth, form.email, form.password);
      const affiliateRef = doc(db, "affiliate_users", credentials.user.uid);
      const affiliateSnap = await getDoc(affiliateRef);

      if (!affiliateSnap.exists()) {
        await upsertAffiliateProfile(credentials.user);
      }

      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const { user } = await signInWithPopup(auth, provider);
      await upsertAffiliateProfile(user);
      toast.success("Signed in with Google.");
    } catch (err) {
      toast.error(err?.message || "Google sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(29,217,175,0.18),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(255,184,77,0.16),transparent_26%)]" />
      <div className="relative grid w-full max-w-5xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-[36px] border border-white/10 bg-[linear-gradient(145deg,rgba(11,18,31,0.94),rgba(8,13,24,0.92))] p-8 shadow-[0_30px_90px_rgba(0,0,0,0.35)]">
          <div className="text-xs uppercase tracking-[0.32em] text-[var(--aff-muted)]">
            IQ Cage partners
          </div>
          <h1 className="mt-3 text-4xl font-black text-white">
            Build campaigns, track turnover, and withdraw commission faster
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-7 text-slate-300">
            The affiliate workspace now syncs your links, referred users, deposits, and turnover tracking into one reporting flow instead of splitting those numbers across unrelated collections.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FeatureCard title="Tracked links" body="Create separate URLs for every ad source or partner campaign." />
            <FeatureCard title="Turnover stats" body="See referred-player turnover and share values without manual checks." />
            <FeatureCard title="Fast payouts" body="Submit withdraw requests from the same dashboard once balance is ready." />
          </div>
        </section>

        <section className="rounded-[36px] border border-white/10 bg-[rgba(11,18,31,0.92)] p-8 shadow-[0_30px_90px_rgba(0,0,0,0.35)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
                {mode === "register" ? "Create account" : "Sign in"}
              </div>
              <h2 className="mt-2 text-3xl font-black text-white">
                {mode === "register" ? "Affiliate registration" : "Affiliate login"}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setMode((current) => (current === "register" ? "login" : "register"))}
              className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-white"
            >
              {mode === "register" ? "Have an account?" : "Need an account?"}
            </button>
          </div>

          <form
            onSubmit={mode === "register" ? handleRegister : handleLogin}
            className="mt-6 space-y-4"
          >
            {mode === "register" ? (
              <>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="Full name" />
                <Input name="country" value={form.country} onChange={handleChange} placeholder="Country" />
                <Input
                  name="refCode"
                  value={form.refCode}
                  onChange={handleChange}
                  placeholder="Referral code (optional)"
                />
              </>
            ) : null}

            <Input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
            />
            <Input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Password"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-[var(--aff-accent)] px-5 py-3 text-sm font-bold text-slate-950 disabled:opacity-60"
            >
              {loading
                ? mode === "register"
                  ? "Creating account..."
                  : "Signing in..."
                : mode === "register"
                ? "Create affiliate account"
                : "Sign in"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-slate-500">
            <div className="h-px flex-1 bg-white/10" />
            or
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] px-5 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            <img
              src="https://img.icons8.com/color/24/000000/google-logo.png"
              alt="Google"
              className="h-5 w-5"
            />
            Continue with Google
          </button>
        </section>
      </div>
    </div>
  );
}

function FeatureCard({ title, body }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-300">{body}</p>
    </div>
  );
}

function Input({ type = "text", ...props }) {
  return (
    <input
      type={type}
      {...props}
      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-slate-500 focus:border-emerald-400/40 focus:outline-none"
      required={type !== "text" || props.name !== "refCode"}
    />
  );
}
